import { useState, useEffect } from "react";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { modelAPI } from "../../lib/api";
import { useLanguage } from "../../context/LanguageContext";
import UserLayout from "../../components/UserLayout";
import ImageUploader from "../../components/ImageUploader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

const AdminModels = () => {
    const { t } = useLanguage();
    const [models, setModels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: "",
        prompt: "",
        thumbnail_url: "",
        reference_image_1: "",
        reference_image_2: "",
        reference_image_3: "",
        tokens: 10,
        is_active: true
    });

    useEffect(() => {
        fetchModels();
    }, []);

    const fetchModels = async () => {
        try {
            const response = await modelAPI.getAll();
            setModels(response.data);
        } catch (error) {
            toast.error(t('loading') + " error");
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.thumbnail_url) {
            toast.error(t('upload_thumbnail'));
            return;
        }
        
        if (!formData.reference_image_1) {
            toast.error(t('upload_reference'));
            return;
        }
        
        setSaving(true);

        try {
            if (editing) {
                await modelAPI.update(editing, formData);
            } else {
                await modelAPI.create(formData);
            }
            toast.success(t('model_saved'));
            setDialogOpen(false);
            fetchModels();
            resetForm();
        } catch (error) {
            toast.error(error.response?.data?.detail || "Error");
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = (model) => {
        setEditing(model.id);
        setFormData({
            name: model.name,
            prompt: model.prompt,
            thumbnail_url: model.thumbnail_url,
            reference_image_1: model.reference_image_1,
            reference_image_2: model.reference_image_2 || "",
            reference_image_3: model.reference_image_3 || "",
            tokens: model.tokens || 10,
            is_active: model.is_active
        });
        setDialogOpen(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t('delete_confirm'))) return;
        
        try {
            await modelAPI.delete(id);
            toast.success(t('model_deleted'));
            fetchModels();
        } catch (error) {
            toast.error("Error");
        }
    };

    const resetForm = () => {
        setEditing(null);
        setFormData({ name: "", prompt: "", thumbnail_url: "", reference_image_1: "", reference_image_2: "", reference_image_3: "", tokens: 10, is_active: true });
    };

    return (
        <UserLayout>
            <div className="space-y-6" data-testid="admin-models">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="font-heading text-2xl font-bold text-gray-900">{t('models_title')}</h1>
                        <p className="text-gray-600">{t('models_subtitle')}</p>
                    </div>
                    <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="rounded-xl" data-testid="add-model-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        {t('add_model')}
                    </Button>
                </div>

                <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>{t('preview')}</TableHead>
                                <TableHead>{t('name')}</TableHead>
                                <TableHead>{t('tokens')}</TableHead>
                                <TableHead>{t('prompt')}</TableHead>
                                <TableHead>{t('references')}</TableHead>
                                <TableHead>{t('status')}</TableHead>
                                <TableHead className="text-right">{t('actions')}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={7} className="text-center py-8">
                                        <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                                    </TableCell>
                                </TableRow>
                            ) : models.length > 0 ? (
                                models.map((model) => (
                                    <TableRow key={model.id}>
                                        <TableCell>
                                            <div className="w-16 h-20 rounded-lg overflow-hidden bg-gray-100">
                                                <img src={model.thumbnail_url} alt={model.name} className="w-full h-full object-cover" />
                                            </div>
                                        </TableCell>
                                        <TableCell className="font-medium">{model.name}</TableCell>
                                        <TableCell>
                                            <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm font-medium">
                                                {model.tokens || 10}
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-gray-500 max-w-xs truncate">{model.prompt}</TableCell>
                                        <TableCell>
                                            <div className="flex -space-x-2">
                                                {model.reference_image_1 && <div className="w-8 h-8 rounded-full border-2 border-white overflow-hidden bg-gray-100"><img src={model.reference_image_1} alt="" className="w-full h-full object-cover" /></div>}
                                                {model.reference_image_2 && <div className="w-8 h-8 rounded-full border-2 border-white overflow-hidden bg-gray-100"><img src={model.reference_image_2} alt="" className="w-full h-full object-cover" /></div>}
                                                {model.reference_image_3 && <div className="w-8 h-8 rounded-full border-2 border-white overflow-hidden bg-gray-100"><img src={model.reference_image_3} alt="" className="w-full h-full object-cover" /></div>}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${model.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                                                {model.is_active ? t('active') : t('inactive')}
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-2">
                                                <Button size="icon" variant="ghost" onClick={() => handleEdit(model)} data-testid={`edit-model-${model.id}`}>
                                                    <Pencil className="w-4 h-4" />
                                                </Button>
                                                <Button size="icon" variant="ghost" className="text-red-500" onClick={() => handleDelete(model.id)} data-testid={`delete-model-${model.id}`}>
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                                        No data
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>

                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle>{editing ? t('edit_model') : t('add_new_model')}</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label>{t('model_name')}</Label>
                                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="model-name-input" />
                            </div>
                            <div className="space-y-2">
                                <Label>{t('token_cost')}</Label>
                                <Input 
                                    type="number" 
                                    value={formData.tokens} 
                                    onChange={(e) => setFormData({ ...formData, tokens: parseInt(e.target.value) || 0 })} 
                                    min={1}
                                    required 
                                    data-testid="model-tokens-input" 
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>{t('ai_prompt')}</Label>
                                <Textarea value={formData.prompt} onChange={(e) => setFormData({ ...formData, prompt: e.target.value })} required placeholder="Describe the model appearance..." data-testid="model-prompt-input" />
                            </div>
                            
                            <div className="space-y-2">
                                <Label>{t('thumbnail')}</Label>
                                <ImageUploader
                                    value={formData.thumbnail_url}
                                    onChange={(url) => setFormData({ ...formData, thumbnail_url: url })}
                                    label="model-thumbnail"
                                />
                            </div>
                            
                            <div className="space-y-2">
                                <Label>{t('reference_required')}</Label>
                                <ImageUploader
                                    value={formData.reference_image_1}
                                    onChange={(url) => setFormData({ ...formData, reference_image_1: url })}
                                    label="model-reference-1"
                                />
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>{t('reference_optional')} 2</Label>
                                    <ImageUploader
                                        value={formData.reference_image_2}
                                        onChange={(url) => setFormData({ ...formData, reference_image_2: url })}
                                        label="model-reference-2"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>{t('reference_optional')} 3</Label>
                                    <ImageUploader
                                        value={formData.reference_image_3}
                                        onChange={(url) => setFormData({ ...formData, reference_image_3: url })}
                                        label="model-reference-3"
                                    />
                                </div>
                            </div>
                            
                            <div className="flex items-center justify-between">
                                <Label>{t('status')} {t('active')}</Label>
                                <Switch checked={formData.is_active} onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })} />
                            </div>
                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>{t('cancel')}</Button>
                                <Button type="submit" disabled={saving} data-testid="save-model-btn">
                                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                                    {saving ? t('loading') : t('save')}
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>
        </UserLayout>
    );
};

export default AdminModels;
